import { useEffect } from 'react';
import { useGameState } from '../../hooks/useGameState.js';
import { usePhaseManager } from '../../hooks/usePhaseManager.js';
import { useAccessibility } from '../../hooks/useAccessibility.js';
import { useAutoSave } from '../../hooks/useAutoSave.js';
import { useAudio } from '../../hooks/useAudio.js';
import { getSeasonId } from '../../utils/time.js';

import { ResourceBar } from '../ui/ResourceBar.jsx';
import { MoraleBar } from '../ui/MoraleBar.jsx';
import { VolumeControls } from '../ui/VolumeControls.jsx';
import { EventCard } from '../phases/EventCard.jsx';
import { AssignCard } from '../phases/AssignCard.jsx';
import { ResultsCard } from '../phases/ResultsCard.jsx';
import { CombatCard } from '../phases/CombatCard.jsx';
import { BuildCard } from '../phases/BuildCard.jsx';
import { GameOverCard } from '../phases/GameOverCard.jsx';
import { TradeDialog } from '../phases/TradeDialog.jsx';

/**
 * Main game component - orchestrates all hooks and components
 */
export default function DiceSettlement({ initialState, onReturnToMenu }) {
  const gameState = useGameState(initialState);
  const { game } = gameState;
  const { announcerRef, announce } = useAccessibility();
  const phaseManager = usePhaseManager(gameState, announce);
  const audio = useAudio();

  // Auto-save on day transitions
  useAutoSave(game);

  // Seasonal ambience based on day, with storm override
  useEffect(() => {
    // Check if storm is active - rain ambience takes priority
    if (game.currentEvent?.type === 'storm') {
      audio.setAmbience('weather_rain');
    } else {
      // Normal seasonal progression
      const seasonId = `ambience_${getSeasonId(game.day)}`;
      audio.setAmbience(seasonId);
    }
  }, [game.day, game.currentEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-200 via-amber-100 to-amber-200 p-4 font-sans">
      {/* Screen reader announcements */}
      <div
        ref={announcerRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      />

      {/* Volume Controls */}
      <VolumeControls />

      {/* Optional menu button */}
      {onReturnToMenu && (
        <button
          onClick={() => {
            if (window.confirm('Return to main menu? Your progress is auto-saved.')) {
              onReturnToMenu();
            }
          }}
          className="absolute top-4 left-4 px-3 py-1 bg-amber-700 hover:bg-amber-800 text-white rounded text-sm transition-colors"
        >
          Menu
        </button>
      )}

      <div className="max-w-lg mx-auto">
        <h1 className="text-3xl font-bold text-center text-amber-900 mb-4 font-serif">
          🎲 Dice Settlement
        </h1>

        <ResourceBar
          day={game.day}
          food={game.food}
          wood={game.wood}
          stone={game.stone}
          metal={game.metal}
          knowledge={game.knowledge}
          techPoints={game.techPoints}
          workers={game.workers}
        />

        <MoraleBar morale={game.morale || 50} />

        <main>
          {/* Building selection modal */}
          {phaseManager.showBuildSelect && (
            <BuildCard {...phaseManager.buildHandlers} />
          )}

          {/* Trade dialog */}
          {phaseManager.showTradeDialog && phaseManager.tradeDialogData && (
            <TradeDialog
              diceTotal={phaseManager.tradeDialogData.diceTotal}
              {...phaseManager.tradeHandlers}
            />
          )}

          {/* Event phase */}
          {!phaseManager.showBuildSelect && game.phase === 'event' && game.currentEvent && (
            <EventCard {...phaseManager.eventHandlers} />
          )}

          {/* Assignment phase */}
          {!phaseManager.showBuildSelect && game.phase === 'assign' && (
            <AssignCard {...phaseManager.assignHandlers} />
          )}

          {/* Results phase */}
          {!phaseManager.showBuildSelect && !phaseManager.showTradeDialog && game.phase === 'results' && (
            <ResultsCard
              {...phaseManager.resultsHandlers}
              assignments={game.assignments}
              workers={game.workers}
            />
          )}

          {/* Combat phase */}
          {!phaseManager.showBuildSelect && game.phase === 'combat' && game.combatResult && (
            <CombatCard {...phaseManager.combatHandlers} />
          )}

          {/* Game over phase */}
          {!phaseManager.showBuildSelect && game.phase === 'gameover' && (
            <GameOverCard {...phaseManager.gameOverHandlers} />
          )}
        </main>
      </div>
    </div>
  );
}
